import React, {useEffect, useRef, useState} from 'react';
import {
    checkNull,
    checkNullArr,
    checkNullJson,
    checkNullStr,
    getArrLen,
    getJsonValue,
    getDefStr,
    isBoolTrue,
    populateArr,
    printError,
    printLog,
    randomInt,
    randomStr
} from '../utils/ReactUtils';

/**
 * Create a tabpane that has tabs in the left and teb-content in the right.
 *
 * @param onChange Callback to be called when a tab is selected/changed and contains: event, index, title, subtitle, tabId
 *
 * @example
 * <SimpleTabPane
 *    tabClassName={`rounded-5 fs-sm-md border-1 border-theme border-solid text-white-hover text-white-active`}
 *    iconClassName={'fs-md'}
 *    tabsWrapperClassName={''}
 *    variant={'theme'}
 *    reverseVariant={'light'}
 *    tabsAt={'start'}
 *    height={'100%'}
 *    defTabIndex={0}
 *    contentClassName={'mt-n2x'}
 *    tabTextAt={'start'}
 *    retainContentInDomOnTabChange={true}
 *    pushUrlParam={false}
 *    onChange={(e, index, tabName) => {
 *        console.log('homeTabPane.tabName:', tabName, 'index:', index);
 *    }}>
 *    {{[
 *       {
 *           title: 'Tab-1',
 *           icon: 'bi bi-icon1',
 *           url: '/tab1',
 *           children: <div>Child 1</div>
 *       },
 *       {
 *           title: 'Tab-2',
 *           icon: 'bi bi-icon2',
 *           children: <div>Child 2</div>
 *       },
 *       {
 *           title: 'Tab-3',
 *           icon: 'bi bi-icon3',
 *           children: <div>Child 3</div>
 *       },
 *    ]}}
 * </SimpleTabPane>
 */
const SimpleTabPane = React.memo(({
                                      className = "",
                                      contentWrapperClass = "",
                                      tabClassName = "",
                                      tabsWrapperClassName = "",
                                      contentClassName = "",
                                      activeTabIndexClass = "active",
                                      transitionVariant = "sm",
                                      titleClassName = "",
                                      subtitleClassName = "",
                                      iconClassName = "",

                                      tabsAt = "start",
                                      tabsBg = "transparent",
                                      contentBg = "",
                                      taggedSubtitle = false,
                                      retainContentInDomOnTabChange = false,
                                      pushUrlParam = false,
                                      urlParamKey = 'param',
                                      urlParamValueKey = 'paramValue',
                                      tabTextAt = "center",
                                      tabTextVertAt = "center",
                                      scrollVariant = "dark",
                                      borderVariant = "danger",
                                      variant = "primary",
                                      reverseVariant = "white",
                                      activeBorder = true,
                                      arrows = true,
                                      scrolledContent = true,
                                      defTabIndex = 0,
                                      tabGap = 2,
                                      subtitleHeight,
                                      subtitleFontSize,
                                      tabFontSize = "16px",

                                      style = {},
                                      tabStyle = {},
                                      contentStyle = {},
                                      contentWrapperStyle = {},
                                      tabsWrapperStyle = {},
                                      titleStyle = {},
                                      subtitleStyle = {},
                                      iconStyle = {},

                                      iconSize = "10px",

                                      height = "auto",
                                      children = [],
                                      content,
                                      onChange//returns e, index, title, subtitle
                                  }) => {
    const TAG = "SimpleTabPane";

    const [activeTabIndex, setActiveTabIndex] = useState(defTabIndex);
    const [tabActiveData, setTabActiveData] = useState({name: null, active: false});
    const [tabHoverData, setTabHoverData] = useState({name: null, active: false});
    const [tabPressData, setTabPressData] = useState({name: null, active: false});
    const [tabIds, setTabIds] = useState([]);

    const tabsParentRef = useRef([]);
    const tabItemRefs = useRef([]);

    const [viewHeight, setViewHeight] = useState(height);

    useEffect(() => {
        setViewHeight(height);

        //change height to auto by calculating the heights of it's children
        if (height === "auto") {
            try {
                const children = tabsParentRef.current.children;
                let totalHeight = 0;
                for (let i = 0; i < children.length; i++) {
                    // totalHeight += children[i].offsetHeight + children.paddingTop + children.paddingBottom;
                    totalHeight += children[i].offsetHeight;
                }
                setViewHeight(totalHeight + 50 + "px");
                // tabsParentRef.current.style.height = totalHeight + "px";
            } catch (e) {
                logErr(e);
            }
        }
    }, [height]);

    useEffect(() => {
        setActiveTabIndex(defTabIndex);
    }, [defTabIndex]);

    useEffect(() => {
        setActiveTabIndex(defTabIndex);
        try {
            tabItemRefs.current[defTabIndex].click();
            tabItemRefs.current[defTabIndex].focus();
        } catch (error) {
            logErr('useEffect.defTabIndex.error:', error);
        }
    }, [defTabIndex]);

    useEffect(() => {
        setTabIds(populateArr(tabIds, randomStr(randomInt(10, 20)), getArrLen(children)));
    }, [children]);

    function pushCurrTabUrlParam(childIndexItem = {}) {
        const fun = 'pushCurrTabUrlParam:';
        if (isBoolTrue(pushUrlParam) && checkNullJson(childIndexItem) && checkNullArr(children) && checkNullStr(urlParamKey) && checkNullStr(urlParamValueKey)) {
            try {
                const paramValueToSet = childIndexItem[urlParamValueKey];
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.set(urlParamKey, paramValueToSet);
                window.history.replaceState({}, '', window.location.pathname + '?' + urlParams.toString());
                log(fun, 'paramValueToSet:', paramValueToSet);
            } catch (error) {
                logErr(fun, 'error:', error);
            }
        }
    }

    function handleTabsKeyDown(e) {
        if (isBoolTrue(arrows)) {
            try {
                if (checkNullArr(children)) {
                    const tabSize = children.length;
                    if (e.key === "ArrowLeft") {
                        e.preventDefault();
                        const activeIndex = (activeTabIndex - 1 + tabSize) % tabSize;
                        setActiveTabIndex(activeIndex);
                        setTabActiveData({name: getJsonValue(children[activeIndex], 'title'), active: true});
                        tabItemRefs.current[activeIndex].click();
                        tabItemRefs.current[activeIndex].focus();
                        pushCurrTabUrlParam(children[activeIndex]);
                    } else if (e.key === "ArrowRight") {
                        e.preventDefault();
                        const activeIndex = ((activeTabIndex + 1 + tabSize) % tabSize);
                        setTabActiveData({name: getJsonValue(children[activeIndex], 'title'), active: true});
                        setActiveTabIndex(activeIndex);
                        tabItemRefs.current[activeIndex].click();
                        tabItemRefs.current[activeIndex].focus();
                        pushCurrTabUrlParam(children[activeIndex]);
                    }
                }
            } catch (error) {
                logErr(error)
            }
        }
    }

    const handleTabClick = (e, index, title, subtitle) => {
        if (index != activeTabIndex) {
            setActiveTabIndex(index);
            setTabActiveData({name: title, active: true});
            // log( `handleTabClick=> index: ${index}, title: ${title}, subtitle: ${subtitle}`);
            if (checkNull(onChange)) onChange(e, index, title, subtitle);
            pushCurrTabUrlParam(children[index]);
        }
    };

    return (
        <div className={`tab-layout simple-tabpane p-0 m-0  ${className}`}
             style={{...style}}
        >
            {/*tabs*/}
            <div
                ref={tabsParentRef}
                tabIndex="0"
                onKeyDown={handleTabsKeyDown}
                className={`d-flex flex-nowrap jc-${tabsAt} px-1x al-center fd-row outline-none border-none scroller none scroller-x-auto scroll-${scrollVariant} w-100 p-0 m-0 pb-1 pe-3 ${tabsWrapperClassName} bg-${tabsBg}`}
                style={{
                    viewHeight,
                    backgroundColor: tabsBg,
                    ...tabsWrapperStyle
                }}>
                {/*tab-items*/}
                {checkNullArr(children) && children.map((item, index) => {
                    const tabId = index + item.title;
                    const isActive = index === activeTabIndex;
                    return <div
                        ref={(ref) => (tabItemRefs.current[index] = ref)}
                        key={index + tabId}
                        id={tabId}
                        tabIndex="-1"
                        style={{fontSize: tabFontSize, ...tabStyle}}
                        className={`tab ${isBoolTrue(activeBorder && isActive) ? 'active rounded-2' : 'borderless'} 
                        m-0 px-3 py-1 d-flex fd-row flex-nowrap
                        ${index > 0 ? `ms-${tabGap}` : ''}
                        text-${tabTextAt} 
                        jc-${tabTextAt}  
                        al-${tabTextVertAt}
                         ${isActive ? activeTabIndexClass : ''} 
                        border-${borderVariant}x
                        d-flex w-100 transition-${transitionVariant} select-none cursor-pointer
                         text-${variant}-hover text-${reverseVariant}-active text-${reverseVariant}-focus text-${variant} highlight-${variant}-hover highlight-${variant}-full-focus highlight-${variant}-full-visited highlight-${variant}-full-active outline-none outline-none border-nonex
                        ${tabClassName}
                        ${getJsonValue(item, 'titleClassName', '')}
                         ${isBoolTrue(getJsonValue(item, 'disabled', false)) ? 'disable-item-xs' : ''}
                        `}
                        disabled={isBoolTrue(getJsonValue(item, 'disabled', false))}
                        onClick={(e) => handleTabClick(e, index, item.title, item.subtitle)}
                    >
                        {/*tab-text*/}
                        <span className={`w-100 d-flex fd-row overflow-hidden jc-between alx-${checkNullStr(item.subtitle) ? 'start' : 'center'}`}>
                            <span className={`w-auto d-flex fd-row overflow-hidden jc-start flex-nowrap al-center`}>
                                {/*icon*/}
                                {checkNullStr(getDefStr(item.iconClass, item.icon)) &&
                                    <i className={`p-0 m-0 mx-1 me-2 d-flex jc-center al-center ${getDefStr(item.iconClass, item.icon)} ${iconClassName}`}
                                       style={{
                                           fontSize: iconSize,
                                           ...iconStyle,
                                           ...checkNullJson(item.iconStyle) ? item.iconStyle : {}
                                       }}/>
                                }
                                {/*title*/}
                                <span
                                    className={`text-nowrap p-0 m-0 ${titleClassName}`}
                                    style={{...titleStyle}}>
                                    {checkNullStr(item.renderTitle) ? item.renderTitle : item.title}
                                </span>
                            </span>

                            {/*subtitle*/}
                            {checkNullStr(item.subtitle) &&
                                <div className={`subtitle m-0 px-1 ${subtitleClassName} 
                                bg-${getJsonValue(item, 'subtitleBg', '')}
                                text-${getJsonValue(item, 'subtitleColor', '')}
                                ${getJsonValue(item, 'subtitleClassName', '')}
                                ${isBoolTrue(taggedSubtitle) ? 'p-1 circular d-flex jc-center align-items-center' : ''}
                                `}
                                     style={{
                                         height: isBoolTrue(taggedSubtitle) ? getDefStr(subtitleHeight, getJsonValue(item, 'subtitleHeight', '16px')) : "auto",
                                         fontSize: isBoolTrue(taggedSubtitle) ? getDefStr(subtitleFontSize, getJsonValue(item, 'subtitleFontSize', '10px')) : "inherit",
                                         ...subtitleStyle
                                     }}>
                                    {item.subtitle}
                                </div>}
                        </span>
                    </div>
                })}
            </div>

            {/*tab-content*/}
            <div className={`tab-contentx w-100 bg-${contentBg} p-0 m-0 mt-1 ${contentClassName}`}
                 style={contentStyle}>
                {checkNullArr(children) && children.map((item, index) => {
                    const isActive = index === activeTabIndex;
                    return isBoolTrue(retainContentInDomOnTabChange)
                        ? <div
                            className={`p-0 m-0 ${contentWrapperClass} ${isBoolTrue(getJsonValue(item, 'disabled', false)) ? 'disable-item-xs' : ''} ${isBoolTrue(scrolledContent) ? `scroller overflow-y-auto scroll-${scrollVariant}` : ''}  ${isActive ? 'active' : ''}`}
                            key={index + 'kk'}
                            style={{
                                display: (isActive) ? 'block' : 'none',
                                height: viewHeight,
                                ...contentWrapperStyle
                            }}
                            disabled={isBoolTrue(getJsonValue(item, 'disabled', false))}
                        >
                            {item.topContent && item.topContent}
                            {item.children}
                            {item.bottomContent && item.bottomContent}
                        </div>
                        : ((isActive) &&
                            <div
                                className={`p-0 m-0 inner-content ${contentWrapperClass} ${isBoolTrue(getJsonValue(item, 'disabled', false)) ? 'disable-item-xs' : ''} ${isBoolTrue(scrolledContent) ? `scroller overflow-y-auto scroll-${scrollVariant}` : ''}  ${isActive ? 'active' : ''}`}
                                disabled={isBoolTrue(getJsonValue(item, 'disabled', false))}
                                key={index + 'll'}
                                style={{
                                    height: viewHeight,
                                    ...contentWrapperStyle
                                }}
                            >
                                {item.topContent && item.topContent}
                                {item.children}
                                {item.bottomContent && item.bottomContent}
                            </div>)
                })}
            </div>
            {content}
        </div>
        // </div>
    );

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});

export default SimpleTabPane;
